import {
  isNil, round, sum,
} from 'lodash/fp';
import { useContext } from 'react';
import { CurrencyCodes } from '@onevesthq/ov-enums';
import { useTranslation } from 'react-i18next';
import { formatMoneyValue, formatPercentValue, getBookCostCents } from '../../../../../util';
import { TableCell, TableTotalCell } from '../../../../2-component';
import { useThemeTokens } from '../../../../../providers/themeTokenProvider';
import { currencySymbol } from '../../../../../util/currency';
import { UserContext } from '../../../../../providers/userContextProvider';
import { Holding } from '../../../../../interfaces';
import {
  find1Day, find1Month, find1Week, find3Day,
} from './tableHoldings';
import { BookValueType } from '../../../../../providers/statsHooks';
import { CurrencyFlag } from '../../../../3-pattern';

export const SecurityTotalHoldingCell = ({
  holdings, type, history, missesFxRate, hasMultipleCurrencies, isFirstColumn, dataTestId = 'security-total-holding-cell', bookValueType,
  isGrouped = false, currencyCode, totalMarketValue,
}: {
  holdings: Holding[], type: string, history: any[], missesFxRate?: boolean, hasMultipleCurrencies?: boolean, isFirstColumn?: boolean, dataTestId?: string,
  bookValueType?: BookValueType, isGrouped?: boolean, currencyCode?: CurrencyCodes, totalMarketValue?: number,
}) => {
  const { t } = useTranslation(['client', 'accountTypes']);
  const { sys } = useThemeTokens();
  const { activeCurrency } = useContext(UserContext);

  const oneDayTotal = () => round(sum(holdings.filter((x: Holding) => !x.financialProduct?.isCash).map((a: Holding) => {
    const oneDayFromHistory = find1Day(a, history);
    const eodTotal = (a.eodPriceCents ?? 0) * (a.quantity ?? 0);
    const total = (a.totalCents ?? 0) - (oneDayFromHistory?.totalCents ?? eodTotal);
    return total;
  })));
  const threeDayTotal = () => round(sum(holdings.map((a: Holding) => (a.totalCents ?? 0) - (find3Day(a, history)?.totalCents ?? 0))));
  const oneWeekTotal = () => round(sum(holdings.map((a: Holding) => (a.totalCents ?? 0) - (find1Week(a, history)?.totalCents ?? 0))));
  const oneMonthTotal = () => round(sum(holdings.map((a: Holding) => (a.totalCents ?? 0) - (find1Month(a, history)?.totalCents ?? 0))));

  const getTotalBookValue = (): string => {
    const currency: CurrencyCodes = holdings.length > 0 ? (holdings[0].currency ?? CurrencyCodes.CAD) : CurrencyCodes.CAD;
    const cumulativeValue = holdings.reduce((acc, holding) => {
      const value = getBookCostCents(holding, bookValueType);
      return acc + value;
    }, 0);
    return formatMoneyValue(cumulativeValue, currencySymbol[currency] ?? '$', 2, false);
  };

  const leadingComponent = isGrouped ? currencyCode && <CurrencyFlag baseCurrency={currencyCode} /> : undefined;

  const totalBookCostCents = holdings?.reduce((p, h) => (
    p + getBookCostCents(h, bookValueType)
  ), 0) ?? 0;
  const totalInvestedMarketValueCents = holdings?.reduce((p, h) => (
    p + (!h.financialProduct?.isCash && !!h.adjustedCostBaseCents ? (h.totalCents ?? 0) : 0)
  ), 0) ?? 0;
  const unrealizedGainsAndLossCents = totalInvestedMarketValueCents - totalBookCostCents;
  const unrealizedGainsAndLossPercent = totalBookCostCents !== 0 ? (unrealizedGainsAndLossCents / Math.abs(totalBookCostCents)) : 0;

  const getUnrealizedGainsAndLoss = () => (
    <>
      {`${formatMoneyValue(unrealizedGainsAndLossCents, currencySymbol[activeCurrency as CurrencyCodes], 2, false)}
      (${formatPercentValue(unrealizedGainsAndLossPercent)})`}
    </>
  );

  const getPercentOfTotal = () => {
    if (missesFxRate) {
      return '-';
    }

    if (isGrouped) {
      if (!totalMarketValue) {
        return undefined;
      }

      const totalPercentage = holdings
        .filter((a: Holding) => (a.totalCents ?? 0) >= 0)
        .reduce((total, a) => total + (a.totalCents ?? 0) / totalMarketValue, 0);

      return formatPercentValue(totalPercentage);
    }

    return '100%';
  };

  switch (type) {
    case 'symbol':
    case 'name':
    case 'securityName':
    case 'quantity':
    case 'bookCostNative':
    case 'bookCost':
    case 'currentPriceNative':
    case 'currentPrice':
    case 'type':
      return (isFirstColumn
        ? <TableTotalCell testId={dataTestId} isFirst={isFirstColumn} isGrouped={isGrouped} leadingComponent={leadingComponent}>
            {isGrouped ? currencyCode : t('accountTable.total')}
          </TableTotalCell>
        : <TableTotalCell testId={dataTestId} isFirst={isFirstColumn} isGrouped={isGrouped} right>
            {isGrouped ? '-' : ''}
          </TableTotalCell>
      );
    case 'oneDayChange':
      return (
        missesFxRate ? (
          <TableTotalCell testId={dataTestId} isFirst={isFirstColumn} isGrouped={isGrouped} right>
            {isGrouped ? '-' : ''}
          </TableTotalCell>
        ) : (
          <TableTotalCell testId={dataTestId} number isFirst={isFirstColumn} isGrouped={isGrouped} sx={{
            textAlign: 'right',
            color: oneDayTotal() === 0 ? sys.color.onSurface : oneDayTotal() >= 0 ? sys.color.positive : sys.color.negative,
          }}>
            {`${formatMoneyValue(oneDayTotal(), currencySymbol[activeCurrency as CurrencyCodes], 2, false)}
            (${formatPercentValue(oneDayTotal() / sum(holdings.map((a: Holding) => ((a.totalCents ?? 0)))))})`
            }
          </TableTotalCell>
        )
      );
    case 'threeDayChange':
      return (
        missesFxRate ? (
          <TableTotalCell testId={dataTestId} isFirst={isFirstColumn} isGrouped={isGrouped} right
          >
            {isGrouped ? '-' : ''}
          </TableTotalCell>
        ) : (
          <TableTotalCell testId={dataTestId} number isFirst={isFirstColumn} isGrouped={isGrouped} sx={{
            textAlign: 'right',
            color: threeDayTotal() === 0 ? sys.color.onSurface : threeDayTotal() >= 0 ? sys.color.positive : sys.color.negative,
          }}>
            {`${formatMoneyValue(threeDayTotal(), currencySymbol[activeCurrency as CurrencyCodes], 2, false)}
            (${formatPercentValue(threeDayTotal() / sum(holdings.map((a: Holding) => ((a.totalCents ?? 0)))))})`}
          </TableTotalCell>
        )
      );
    case 'oneWeekChange':
      return (
        missesFxRate ? (
          <TableTotalCell testId={dataTestId} isFirst={isFirstColumn} isGrouped={isGrouped} right>
            {isGrouped ? '-' : ''}
          </TableTotalCell>
        ) : (
          <TableTotalCell testId={dataTestId} number isFirst={isFirstColumn} isGrouped={isGrouped} sx={{
            textAlign: 'right',
            color: oneWeekTotal() === 0 ? sys.color.onSurface : oneWeekTotal() >= 0 ? sys.color.positive : sys.color.negative,
          }}>
            {`${formatMoneyValue(oneWeekTotal(), currencySymbol[activeCurrency as CurrencyCodes], 2, false)}
            (${formatPercentValue(oneWeekTotal() / sum(holdings.map((a: Holding) => ((a.totalCents ?? 0)))))})`}
          </TableTotalCell>
        )
      );
    case 'oneMonthChange':
      return (
        missesFxRate ? (
          <TableTotalCell testId={dataTestId} isFirst={isFirstColumn} isGrouped={isGrouped} right
          >
            {isGrouped ? '-' : ''}
          </TableTotalCell>
        ) : (
          <TableTotalCell testId={dataTestId} number isFirst={isFirstColumn} isGrouped={isGrouped} sx={{
            textAlign: 'right',
            color: oneMonthTotal() === 0 ? sys.color.onSurface : oneMonthTotal() >= 0 ? sys.color.positive : sys.color.negative,
          }}>
            {`${formatMoneyValue(oneMonthTotal(), currencySymbol[activeCurrency as CurrencyCodes], 2, false)}
            (${formatPercentValue(oneMonthTotal() / sum(holdings.map((a: Holding) => ((a.totalCents ?? 0)))))})`}
          </TableTotalCell>
        )
      );
    case 'unrealizedGainLoss':
      return (
        missesFxRate ? (
          <TableTotalCell testId={dataTestId} isFirst={isFirstColumn} isGrouped={isGrouped} right>
            {isGrouped ? '-' : ''}
          </TableTotalCell>
        ) : (
          <TableTotalCell testId={dataTestId} number isGrouped={isGrouped} isFirst={isFirstColumn} sx={{
            textAlign: 'right',
            color: unrealizedGainsAndLossCents === 0 ? sys.color.onSurface : unrealizedGainsAndLossCents > 0 ? sys.color.positive : sys.color.negative,
          }}>
            {getUnrealizedGainsAndLoss()}
          </TableTotalCell>
        )
      );
    case 'percentOfTotal':
      return (
        <TableTotalCell testId={dataTestId} number isFirst={isFirstColumn} right isGrouped={isGrouped}>
          {getPercentOfTotal()}
        </TableTotalCell>
      );
    case 'currentValue':
    case 'marketValueNative':
      return (
        hasMultipleCurrencies ? (
          <TableTotalCell testId={dataTestId} isFirst={isFirstColumn} isGrouped={isGrouped} right>
            {isGrouped ? '-' : ''}
          </TableTotalCell>
        ) : (
          <TableTotalCell testId={dataTestId} right bold number isFirst={isFirstColumn} isGrouped={isGrouped}>
            {formatMoneyValue(sum(holdings.map((a: Holding) => (a?.originalCurrency?.totalCents ?? a.totalCents) ?? 0)), currencySymbol[activeCurrency as CurrencyCodes], 2, false)}
          </TableTotalCell>
        )
      );
    case 'marketValue':
      return (
        <TableTotalCell testId={dataTestId} right bold number isGrouped={isGrouped}>
          {holdings.every((a: Holding) => !isNil(a?.originalCurrency))
            ? formatMoneyValue(sum(holdings.map((a: Holding) => a.totalCents)), currencySymbol[activeCurrency as CurrencyCodes], 2, false)
            : '-'
          }
        </TableTotalCell>
      );
    case 'bookValue':
      return (
        <TableTotalCell testId={dataTestId} right bold number isGrouped={isGrouped}>
          {getTotalBookValue()}
        </TableTotalCell>
      );
    default:
      return (
        <TableCell testId={dataTestId}>
          {type}
        </TableCell>
      );
  }
};
